// src/pages/patient/PatientAppointments.jsx
//
// /patient/appointments -- the one appointment list.
//
//   * tabs Upcoming / Current / Past / Cancelled switch immediately;
//   * search (debounced) plus compact filter chips -- Status, Date, Doctor,
//     Clinic, Service and Consultation type -- each opening its own panel;
//     active filters are listed as removable chips with "Clear all";
//   * tab, filters, page and list/calendar view live in the URL, so a refresh,
//     a shared link or Back from a detail page restores the same context;
//   * any filter change returns to page 1; a page that no longer exists after
//     results shrink moves to the last real page;
//   * every request is sequenced and aborted when superseded, so a slow older
//     response never replaces a newer one; a background refresh keeps the
//     current results on screen;
//   * totals and pages come from the server -- nothing is counted from the page;
//   * every action shown comes from the server's `allowed_actions`.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { readApiError } from "../../lib/apiErrors";
import { browserTimezone, formatLocalDate, formatTimeInZone, todayInZone } from "../../lib/displayTimezone";
import useSequencedRequest from "../../lib/useSequencedRequest";
import { appointmentWorkspaceApi } from "../../services/patientAppointmentWorkspace";
import { InlineError, Pill, TimezoneNotice, classNames, modeLabel, modeTone, money } from "../../components/patient/booking/bookingUi";
import FilterChips, { ActiveFilterChips, ChipOptions } from "../../components/patient/appointments/FilterChips";

export const LIST_DEBOUNCE_MS = 350;
export const BACKGROUND_REFRESH_MS = 60000;
const PAGE_SIZE = 10;
const MAX_RANGE_DAYS = 366;

export const TABS = [
  ["upcoming", "Upcoming"],
  ["current", "Current"],
  ["past", "Past"],
  ["cancelled", "Cancelled"],
];

const FILTER_KEYS = ["q", "status", "doctor_id", "clinic_id", "service_id", "mode", "date_from", "date_to"];
const EMPTY_FILTERS = { q: "", status: "", doctor_id: "", clinic_id: "", service_id: "", mode: "", date_from: "", date_to: "" };

export const STATUS_TONES = {
  pending_payment: "warn",
  booked: "info",
  confirmed: "success",
  checked_in: "violet",
  in_progress: "violet",
  completed: "success",
  no_show: "danger",
  cancelled_by_patient: "danger",
  cancelled_by_clinic: "danger",
  cancelled_by_doctor: "danger",
  refunded: "neutral",
};

function daysInclusive(from, to) {
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = to.split("-").map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000) + 1;
}

function rangeProblem(from, to) {
  if (!from || !to) return "";
  if (to < from) return "The end date must be on or after the start date.";
  if (daysInclusive(from, to) > MAX_RANGE_DAYS) return `Choose a date range of up to ${MAX_RANGE_DAYS} days.`;
  return "";
}

function filtersFromParams(params) {
  const out = { ...EMPTY_FILTERS };
  FILTER_KEYS.forEach((key) => {
    out[key] = params.get(key) || "";
  });
  return out;
}

function positivePage(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : 1;
}

export function buildListParams({ tab, filters, page, view }) {
  const params = new URLSearchParams();
  params.set("tab", tab);
  FILTER_KEYS.forEach((key) => {
    if (filters[key]) params.set(key, filters[key]);
  });
  if (page > 1) params.set("page", String(page));
  if (view === "calendar") params.set("view", "calendar");
  return params;
}

function optionLabel(options, value) {
  if (!value) return "";
  return (options || []).find((o) => String(o.value) === String(value))?.label || "Selected";
}

function shortDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-CA", { day: "numeric", month: "short", timeZone: "UTC" });
}

function dateValueLabel(from, to) {
  if (from && to) return `${shortDate(from)} – ${shortDate(to)}`;
  if (from) return `From ${shortDate(from)}`;
  if (to) return `Until ${shortDate(to)}`;
  return "";
}

function DateBlock({ startAt, timeZone }) {
  const day = todayInZone(timeZone, new Date(startAt));
  const [y, m, d] = day.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return (
    <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-2xl bg-primary/10 text-primary" aria-hidden="true">
      <span className="text-[11px] font-bold uppercase">{date.toLocaleDateString("en-CA", { month: "short", timeZone: "UTC" })}</span>
      <span className="text-2xl font-bold leading-none">{d}</span>
    </div>
  );
}

function AppointmentCard({ item, timeZone, listSearch }) {
  const actions = item.allowed_actions || {};
  return (
    <li className="rounded-3xl border border-border bg-white p-4 shadow-soft sm:p-5" data-testid="appointment-card">
      <div className="flex items-start gap-4">
        <DateBlock startAt={item.start_at} timeZone={timeZone} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-text">{item.service_name}</h3>
              <p className="truncate text-sm text-muted">{item.doctor_name}{item.clinic_name ? ` · ${item.clinic_name}` : ""}</p>
            </div>
            <Pill tone={STATUS_TONES[item.status] || "neutral"}>{item.status_label}</Pill>
          </div>
          <p className="mt-1 text-sm font-medium text-text">
            {formatLocalDate(todayInZone(timeZone, new Date(item.start_at)))} · {formatTimeInZone(item.start_at, timeZone)}
            {item.end_at ? ` – ${formatTimeInZone(item.end_at, timeZone)}` : ""}
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-sm">
            <Pill tone={modeTone(item.consultation_mode)}>{modeLabel(item.consultation_mode) || item.consultation_mode}</Pill>
            {item.location_name ? <Pill tone="neutral">{item.location_name}</Pill> : null}
            <Pill tone="neutral">{money(item.price, item.currency)}</Pill>
          </div>
          <p className="mt-2 text-xs text-muted">{item.appointment_number}</p>
        </div>
      </div>
      {item.payment?.reserved_payment_required ? (
        <p className="mt-3 rounded-2xl bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800">
          {item.payment.label}
          {item.payment.payment_deadline ? ` · pay by ${formatTimeInZone(item.payment.payment_deadline, timeZone)}` : ""}
        </p>
      ) : null}
      {item.pending_required_forms_count > 0 ? (
        <p className="mt-2 text-sm text-slate-700">{item.pending_required_forms_count} required form{item.pending_required_forms_count === 1 ? "" : "s"} to complete</p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to={`/patient/appointments/${item.id}`} state={{ listSearch }} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50">
          View details
        </Link>
        {actions.can_retry_payment ? (
          <Link to={`/patient/appointments/${item.id}`} state={{ listSearch }} className="rounded-2xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white">Pay now</Link>
        ) : null}
        {actions.can_join_video ? (
          <Link to={`/patient/appointments/${item.id}#video`} state={{ listSearch }} className="rounded-2xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white">Join video</Link>
        ) : null}
      </div>
    </li>
  );
}

export default function PatientAppointments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const tab = TABS.some(([key]) => key === urlTab) ? urlTab : "upcoming";
  const [timeZone] = useState(() => browserTimezone() || "UTC");
  const [filters, setFilters] = useState(() => filtersFromParams(searchParams));
  const [settled, setSettled] = useState(filters);
  const [page, setPage] = useState(() => positivePage(searchParams.get("page")));
  const [view, setView] = useState(() => (searchParams.get("view") === "calendar" ? "calendar" : "list"));
  const [openChip, setOpenChip] = useState(null);

  const list = useSequencedRequest(null);
  const options = useSequencedRequest(null);
  const runList = list.run;
  const runOptions = options.run;
  const problem = rangeProblem(filters.date_from, filters.date_to);
  const settledProblem = rangeProblem(settled.date_from, settled.date_to);
  const settledKey = JSON.stringify(settled);

  // Typing settles for LIST_DEBOUNCE_MS; any settled change returns to page 1.
  useEffect(() => {
    if (JSON.stringify(filters) === settledKey) return undefined;
    const id = window.setTimeout(() => {
      setSettled(filters);
      setPage(1);
    }, LIST_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [filters, settledKey]);

  // Mirror tab, settled filters, page and view in the URL.
  useEffect(() => {
    const next = buildListParams({ tab, filters: settled, page, view });
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // searchParams is compared, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, settledKey, page, view]);

  const load = useCallback(() => {
    if (settledProblem) return;
    runList(({ signal }) =>
      appointmentWorkspaceApi.list({ tab, ...settled, display_timezone: timeZone, page, page_size: PAGE_SIZE }, { signal })
    );
    // settledKey captures every settled value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runList, tab, settledKey, timeZone, page, settledProblem]);

  useEffect(() => {
    load();
  }, [load]);

  const loadOptions = useCallback(() => runOptions(({ signal }) => appointmentWorkspaceApi.filterOptions({ signal })), [runOptions]);
  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    const id = window.setInterval(() => load(), BACKGROUND_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  function selectTab(next) {
    if (next === tab) return;
    setPage(1);
    setSearchParams(buildListParams({ tab: next, filters: settled, page: 1, view }), { replace: true });
  }

  // Text and dates settle (debounced); chip choices apply at once and also
  // flush any pending search text, so exactly one request follows.
  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function applyFilters(changes) {
    const next = { ...filters, ...changes };
    setFilters(next);
    setSettled(next);
    setPage(1);
  }

  function clearFilters() {
    setFilters(EMPTY_FILTERS);
    setSettled(EMPTY_FILTERS);
    setPage(1);
    setOpenChip(null);
  }

  const data = list.data;
  // Results from a different tab are never shown under this one.
  const current = data && data.tab === tab ? data : null;
  const items = useMemo(() => current?.items || [], [current]);

  // The requested page no longer exists (results shrank): go to the last one.
  useEffect(() => {
    if (!current || list.loading) return;
    const pages = Number(current.total_pages || 0);
    if (current.total > 0 && !items.length && page > pages && pages > 0) setPage(pages);
  }, [current, items, page, list.loading]);

  const opt = options.data || {};
  const listSearch = buildListParams({ tab, filters: settled, page, view }).toString();
  const filtersActive = FILTER_KEYS.some((key) => settled[key]);
  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const day = todayInZone(timeZone, new Date(item.start_at));
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(item);
    });
    return Array.from(map.entries());
  }, [items, timeZone]);

  const inputClass = "mt-1 w-full rounded-2xl border border-border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30";

  const optionChip = (key, field, label, icon, list, allLabel) => ({
    key,
    label,
    icon,
    active: !!settled[field],
    valueLabel: optionLabel(list, settled[field]),
    render: (close) => <ChipOptions name={label} value={settled[field]} options={list} allLabel={allLabel} onChange={(v) => applyFilters({ [field]: v })} close={close} />,
  });

  const chips = [
    optionChip("status", "status", "Status", "status", opt.statuses, "All statuses"),
    {
      key: "date",
      label: "Date",
      icon: "date",
      active: !!(settled.date_from || settled.date_to),
      valueLabel: dateValueLabel(settled.date_from, settled.date_to),
      render: (close) => (
        <div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="filter-from" className="text-xs font-semibold text-muted">From</label>
              <input id="filter-from" type="date" value={filters.date_from} onChange={(e) => updateFilter("date_from", e.target.value)}
                aria-invalid={problem ? "true" : undefined} aria-describedby={problem ? "filter-range-error" : undefined} className={inputClass} />
            </div>
            <div>
              <label htmlFor="filter-to" className="text-xs font-semibold text-muted">To</label>
              <input id="filter-to" type="date" value={filters.date_to} min={filters.date_from || undefined} onChange={(e) => updateFilter("date_to", e.target.value)}
                aria-invalid={problem ? "true" : undefined} aria-describedby={problem ? "filter-range-error" : undefined} className={inputClass} />
            </div>
          </div>
          <p className="mt-2 text-xs text-muted">Dates are in {timeZone}.</p>
          <div className="mt-3 flex justify-between gap-2">
            <button type="button" onClick={() => applyFilters({ date_from: "", date_to: "" })} className="rounded-xl px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Any date</button>
            <button type="button" onClick={() => close()} className="rounded-xl bg-primary px-3 py-1.5 text-sm font-semibold text-white">Done</button>
          </div>
        </div>
      ),
    },
    optionChip("doctor", "doctor_id", "Doctor", "doctor", opt.doctors, "All doctors"),
    optionChip("clinic", "clinic_id", "Clinic", "clinic", opt.clinics, "All clinics"),
    optionChip("service", "service_id", "Service", "service", opt.services, "All services"),
    optionChip("mode", "mode", "Type", "mode", opt.modes, "All types"),
  ];

  const removable = [
    settled.q ? { key: "q", label: "Search", value: settled.q, onRemove: () => applyFilters({ q: "" }) } : null,
    settled.status ? { key: "status", label: "Status", value: optionLabel(opt.statuses, settled.status), onRemove: () => applyFilters({ status: "" }) } : null,
    settled.date_from || settled.date_to
      ? { key: "date", label: "Date", value: dateValueLabel(settled.date_from, settled.date_to), onRemove: () => applyFilters({ date_from: "", date_to: "" }) }
      : null,
    settled.doctor_id ? { key: "doctor", label: "Doctor", value: optionLabel(opt.doctors, settled.doctor_id), onRemove: () => applyFilters({ doctor_id: "" }) } : null,
    settled.clinic_id ? { key: "clinic", label: "Clinic", value: optionLabel(opt.clinics, settled.clinic_id), onRemove: () => applyFilters({ clinic_id: "" }) } : null,
    settled.service_id ? { key: "service", label: "Service", value: optionLabel(opt.services, settled.service_id), onRemove: () => applyFilters({ service_id: "" }) } : null,
    settled.mode ? { key: "mode", label: "Type", value: optionLabel(opt.modes, settled.mode), onRemove: () => applyFilters({ mode: "" }) } : null,
  ].filter(Boolean);

  return (
    <div className="mx-auto w-full max-w-6xl py-2 sm:py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-text">My appointments</h1>
          <TimezoneNotice timeZone={timeZone} className="mt-1" />
        </div>
        <Link to="/patient/book-appointment" className="btn-primary-gradient rounded-2xl px-5 py-3 text-sm font-semibold text-white">Book appointment</Link>
      </div>

      <div className="mt-5 rounded-3xl border border-border bg-white p-4 shadow-soft sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div role="tablist" aria-label="Appointment groups" className="flex gap-1 overflow-x-auto rounded-2xl bg-slate-100 p-1">
            {TABS.map(([key, label]) => (
              <button key={key} type="button" role="tab" id={`tab-${key}`} aria-selected={tab === key} aria-controls="appointments-panel" onClick={() => selectTab(key)}
                className={classNames("whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold transition", tab === key ? "bg-white text-primary shadow-sm" : "text-slate-600 hover:text-text")}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-1 self-start rounded-2xl bg-slate-100 p-1 lg:self-auto" role="group" aria-label="View">
            <button type="button" aria-pressed={view === "list"} onClick={() => setView("list")} className={classNames("rounded-xl px-3 py-1.5 text-sm font-semibold", view === "list" ? "bg-white text-primary shadow-sm" : "text-slate-600")}>List</button>
            <button type="button" aria-pressed={view === "calendar"} onClick={() => setView("calendar")} className={classNames("rounded-xl px-3 py-1.5 text-sm font-semibold", view === "calendar" ? "bg-white text-primary shadow-sm" : "text-slate-600")}>Calendar</button>
          </div>
        </div>

        <form className="mt-4" onSubmit={(e) => e.preventDefault()} aria-label="Filter appointments" role="search">
          <label htmlFor="appointments-search" className="sr-only">Search</label>
          <input id="appointments-search" type="search" value={filters.q} maxLength={100} onChange={(e) => updateFilter("q", e.target.value)}
            placeholder="Search by reference, service, doctor, clinic or location" className="w-full rounded-2xl border border-border bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
        </form>

        <div className="mt-3">
          <FilterChips chips={chips} openKey={openChip} onOpenChange={setOpenChip} label="Appointment filters" />
          {problem ? <p id="filter-range-error" role="alert" className="mt-2 text-xs font-semibold text-rose-700">{problem}</p> : null}
          <ActiveFilterChips items={removable} onClearAll={clearFilters} />
        </div>
      </div>

      <section id="appointments-panel" role="tabpanel" aria-labelledby={`tab-${tab}`} aria-busy={list.loading || undefined} className="mt-5">
        <div className="flex items-center justify-between text-sm text-muted" aria-live="polite">
          <span data-testid="appointments-total">{current ? `${current.total} appointment${current.total === 1 ? "" : "s"}` : ""}</span>
          {list.loading && current ? <span>Refreshing…</span> : null}
        </div>

        {list.error ? <div className="mt-3"><InlineError message={readApiError(list.error, "Appointments could not be loaded.")} onRetry={load} /></div> : null}

        {list.loading && !current ? (
          <ul className="mt-3 grid gap-3 md:grid-cols-2" aria-label="Loading appointments">
            {[0, 1, 2, 3].map((n) => <li key={n} className="h-36 animate-pulse rounded-3xl border border-border bg-white" />)}
          </ul>
        ) : null}

        {current && !items.length && !(current.total > 0) ? (
          <div className="mt-3 rounded-3xl border border-dashed border-border bg-white p-8 text-center text-sm text-muted" data-testid="appointments-empty">
            {filtersActive ? (
              <>
                <p>No appointments match these filters.</p>
                <button type="button" onClick={clearFilters} className="mt-3 rounded-2xl border border-border px-4 py-2 text-sm font-semibold text-text">Clear filters</button>
              </>
            ) : (
              <p>You have no {TABS.find(([key]) => key === tab)?.[1].toLowerCase()} appointments.</p>
            )}
          </div>
        ) : null}

        {items.length && view === "list" ? (
          <ul className={classNames("mt-3 grid gap-3 md:grid-cols-2", list.loading && "opacity-80")}>
            {items.map((item) => <AppointmentCard key={item.id} item={item} timeZone={timeZone} listSearch={listSearch} />)}
          </ul>
        ) : null}

        {items.length && view === "calendar" ? (
          <div className="mt-3 space-y-4" data-testid="appointments-calendar">
            {grouped.map(([day, dayItems]) => (
              <section key={day} aria-label={formatLocalDate(day)}>
                <h2 className="text-sm font-semibold text-text">{formatLocalDate(day)}</h2>
                <ul className="mt-2 grid gap-3 md:grid-cols-2">
                  {dayItems.map((item) => <AppointmentCard key={item.id} item={item} timeZone={timeZone} listSearch={listSearch} />)}
                </ul>
              </section>
            ))}
          </div>
        ) : null}

        {current && current.total_pages > 1 ? (
          <nav className="mt-5 flex items-center justify-between" aria-label="Pagination">
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || list.loading} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">Previous</button>
            <span className="text-sm text-muted">Page {current.page} of {current.total_pages}</span>
            <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= current.total_pages || list.loading} className="rounded-2xl border border-border bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">Next</button>
          </nav>
        ) : null}
      </section>
    </div>
  );
}
